// External libs
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// Constants
import { backend_request_interval_ms } from "../../../constants/BackendRoutes.js";

// Components
import { fetchDeviceReadings} from "../../../services/deviceService.js";
import WidgetTextBox from "./WidgetTextBox.js";
import WidgetWarningCard, {WIDGET_WARNINGCARD_DISPLAY_TYPES} from "../widgetWarningCard/WidgetWarningCard.js";

/**
 * The WidgetWrapperTextBoxLatestReading component.
 * Fetches sensor readings for a device and displays the most recent reading.
 *
 * @param {string} deviceId - Device identifier.
 * @param {string} readingKey - Key of the sensor whose data is to be fetched from the database.
 * @param {string} displayType - Type of the sensor reading for getting the icon and color for the widget.
 * @param {string} description - Description of the sensor reading.
 */
const WidgetWrapperTextBoxLatestReading = ({
  deviceId,
  readingKey,
  displayType,
  description
}) => {
  // State hook to store reading data fetched from the backend.
  const [readingData, setReadingData] = useState([]);

  // State hook to store parsed sensor data for rendering.
  const [sensorData, setSensorData] = useState([]);

    // Effect hook that triggers whenever readingData changes.
  // It updates the sensorData state with parsed data from readingData.
  useEffect(() => {
    if (readingData != null) {
      setSensorData(parseSensorData(readingData));
    }
  }, [readingData]);

  // Effect hook to initiate fetching data when the deviceId changes.
  // Sets up a polling interval to refresh data periodically.
  useEffect(() => {
    let timer;
    let stopped = false;
    const fetchReadingLoop = async() => {
      await fetchDeviceReadingsAndSetReadingData();

      if (!stopped) {
        timer = setTimeout(fetchReadingLoop, backend_request_interval_ms);
      }
    }

    if (deviceId) {
      fetchReadingLoop();
    } else {
      setReadingData(null);
    }
    // Cleanup the interval when the component is unmounted or deviceId changes.
    return () => {
      stopped = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [deviceId]);

  /**
   * Parses the fetched sensor data to extract the most recent reading.
   *
   * @param {Array} data - The raw data fetched from the backend.
   * @returns {Object|null} Parsed sensor data including key, value, and units.
   */
  const parseSensorData = (data) => {
    let result = null;
    if (data.length > 0) {
      result = {
        readingKey: data[0].key,
        units: data[0].units,
        value: data[0].value,
      };
    }
    return result;
  };

  /**
   * Fetches the latest sensor readings from the backend and updates the readingData state.
   *
   * @returns {Promise<void>} A promise that resolves when the data is fetched and state is updated.
   */
  const fetchDeviceReadingsAndSetReadingData = async () => {
  
    // Constant representing the timespan for fetching data (last 24 hours).
    const endDateTime = new Date();
    const startDateTime = new Date(endDateTime.getTime() - 24 * 60 * 60 * 1000);

    const data = await fetchDeviceReadings(deviceId, startDateTime, endDateTime, "last", [
      readingKey,
    ]);
    setReadingData(data);
  };

  /**
   * Generates a message for when no device is selected or the reading is unavailable.
   *
   * @returns {string} The message string to be displayed when no data is available.
   */
  const getEmptySentence = () => {
    let string = `Select device for latest reading of ${readingKey}`;
    string = `${string}...`;
    return string;
  };

  /**
   * Renders the readings or an appropriate message depending on the available data.
   *
   * @param {Array|null} readings - The fetched sensor readings data.
   * @returns {JSX.Element} A message or the WidgetTextBox component displaying sensor data.
   */
  const displayReadings = (readings) => {
    // If no readings are available, show a message prompting the user to select a device.
    if (readings == null) {
      return <div className="widget-text-empty">{getEmptySentence()}</div>;
    }

    // If no valid sensor data is available, display an error message.
    if (
      readings.length == 0 ||
      sensorData == null ||
      sensorData.value == null ||
      sensorData.units == null
    ) {
      return (
        <div className="widget-text-empty">{`Device has no data available for ${readingKey}`}</div>
      );
    }

    // If valid data exists, display the sensor value with its key and units.
    return WIDGET_WARNINGCARD_DISPLAY_TYPES.has(displayType) ? (
        <WidgetWarningCard 
          value={Number(sensorData.value)}
          units={sensorData.units}
          displayType={displayType}
          description={description}
        />
      ) : (
        <WidgetTextBox
          topRow={sensorData.value}
          bottomRow={`${sensorData.readingKey} (${sensorData.units})`}
          description={description}
        />
      );
    };

  // Return the rendered widget with the displayed readings.
  return <div>{displayReadings(readingData)}</div>;
};

WidgetWrapperTextBoxLatestReading.propTypes = {
  deviceId: PropTypes.string.isRequired,
  readingKey: PropTypes.string.isRequired,
  displayType: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

export default WidgetWrapperTextBoxLatestReading;
